import { Stack, Typography, Checkbox, Box, IconButton, TextField, Fab } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ViewOnlyContext } from '../context';
import { Todo } from '../schema';
import DeleteTodoDialog from './DeleteTodoDialog';
import { usePadding } from '../spacing';
import { usePermissions } from '../hooks/usePermissions';

interface Props {
    eventId: string;
    value: Todo[];
    create: (todoData: { todoTypeId: string; mapPinId?: string; areaCoordinates?: string; notes?: string }) => void;
    complete: (todoId: string) => void;
    remove: (todoId: string) => void;
    editNote: (todoId: string, notes: string) => void;
}

interface ItemProps {
    eventId: string;
    value: Todo;
    complete: (todoId: string) => void;
    remove: (todoId: string) => void;
    editNote: (todoId: string, notes: string) => void;
}

export function TodoItem(props: ItemProps) {
    const { eventId, value: todo, complete, editNote } = props;
    const viewOnly = useContext(ViewOnlyContext);
    const location = useLocation();
    const [isEditing, setIsEditing] = useState(false);
    const [editedNotes, setEditedNotes] = useState(todo.notes || '');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const { canEditTodos, canCompleteTodos } = usePermissions();

    const navigate = useNavigate();

    const handleComplete = () => {
        complete(todo.id);
    };

    const handleDelete = () => {
        setDeleteDialogOpen(true);
    };

    const handleSaveNotes = () => {
        editNote(todo.id, editedNotes);
        setIsEditing(false);
    };

    const mapRoutePrefix = viewOnly && location.pathname.startsWith('/viewevent') ? '/viewevent' : '/event';
    const mapPath = `${mapRoutePrefix}/${eventId}/map`;
    const mapTo = viewOnly && location.pathname.startsWith('/viewevent') ? { pathname: mapPath, search: location.search } : mapPath;

    const handleClick = () => {
        if (todo.mapPin) {
            navigate(mapTo, {
                state: {
                    pin: todo.mapPin,
                    mapId: todo.mapPin.map?.id,
                },
            });
        } else if (todo.areaCoordinates) {
            try {
                const parsed = JSON.parse(todo.areaCoordinates as string);
                navigate(mapTo, { state: { sweepArea: parsed } });
            } catch (e) {
                console.error('Failed to parse area coordinates', e);
            }
        }
    };

    const getBackgroundColor = () => {
        if (todo.completed) {
            return '#f5f5f5';
        }
        if (todo.todoType?.color) {
            const hexColor = todo.todoType.color;
            const r = parseInt(hexColor.slice(1, 3), 16);
            const g = parseInt(hexColor.slice(3, 5), 16);
            const b = parseInt(hexColor.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, 0.15)`;
        }
        return 'white';
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                padding: 1.5,
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                marginBottom: 1,
                backgroundColor: getBackgroundColor(),
                transition: 'all 0.2s',
                '&:hover': {
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    borderColor: '#bdbdbd',
                },
                cursor: todo.mapPin || todo.areaCoordinates ? 'pointer' : 'default',
            }}
        >
            <Checkbox
                checked={todo.completed}
                onChange={e => {
                    e.stopPropagation();
                    if (canCompleteTodos) {
                        handleComplete();
                    }
                }}
                onClick={e => e.stopPropagation()}
                disabled={!canCompleteTodos || viewOnly}
                sx={{
                    padding: 0,
                    marginTop: '2px',
                }}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="body1"
                    onClick={handleClick}
                    sx={{
                        fontWeight: 600,
                        textDecoration: todo.completed ? 'line-through' : 'none',
                        color: todo.completed ? 'text.secondary' : 'text.primary',
                        textTransform: 'capitalize',
                        cursor: todo.mapPin || todo.areaCoordinates ? 'pointer' : 'default',
                    }}
                >
                    {todo.todoType?.name || 'Task'}
                    {todo.mapPin && ` - ${todo.mapPin.stationType?.name || 'Station'}`}
                </Typography>

                {isEditing ? (
                    <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        value={editedNotes}
                        onChange={e => setEditedNotes(e.target.value)}
                        placeholder="Add notes..."
                        size="small"
                        autoFocus
                        sx={{ mt: 1 }}
                    />
                ) : (
                    todo.notes && (
                        <Stack
                            color="text.secondary"
                            width={210}
                            sx={{
                                mt: 1,
                                fontStyle: todo.notes ? 'normal' : 'italic',
                            }}
                        >
                            {todo.notes}
                        </Stack>
                    )
                )}
            </Box>

            {!viewOnly && canEditTodos && (
                <Box sx={{ display: 'flex', gap: 0.5, marginLeft: 'auto' }}>
                    {isEditing ? (
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={e => {
                                e.stopPropagation();
                                handleSaveNotes();
                            }}
                        >
                            <SaveIcon fontSize="small" />
                        </IconButton>
                    ) : (
                        <IconButton
                            size="small"
                            onClick={e => {
                                e.stopPropagation();
                                setIsEditing(true);
                            }}
                            sx={{ color: 'text.secondary' }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}

                    <IconButton
                        size="small"
                        color="error"
                        onClick={e => {
                            e.stopPropagation();
                            handleDelete();
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}
            <Box onClick={e => e.stopPropagation()}>
                <DeleteTodoDialog todo={todo} open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} />
            </Box>
        </Box>
    );
}

export default function TodoPage(props: Props) {
    const viewOnly = useContext(ViewOnlyContext);
    const { value, complete, remove, editNote } = props;
    const navigate = useNavigate();
    const { bottomPadding } = usePadding();
    const { canEditTodos } = usePermissions();

    const todosToDisplay = value;

    // filter by todo status (in progress and completed)
    const inProgressTodos = todosToDisplay
        .filter(todo => !todo.completed)
        .sort((a, b) => {
            const dateA = new Date(a.created_at).getTime();
            const dateB = new Date(b.created_at).getTime();
            return dateB - dateA;
        });

    const completedTodos = todosToDisplay
        .filter(todo => todo.completed)
        .sort((a, b) => {
            const dateA = new Date(a.completed_at).getTime();
            const dateB = new Date(b.completed_at).getTime();
            return dateB - dateA;
        });

    const todoList = (
        <Box
            key="viewArea"
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
            }}
        >
            {inProgressTodos.length > 0 && (
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        In Progress
                    </Typography>
                    {inProgressTodos.map(todo => (
                        <TodoItem
                            key={todo.id}
                            eventId={props.eventId}
                            value={todo}
                            complete={complete}
                            remove={remove}
                            editNote={editNote}
                        />
                    ))}
                </Box>
            )}

            {completedTodos.length > 0 && (
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: 'text.secondary',
                        }}
                    >
                        Completed
                    </Typography>
                    {completedTodos.map(todo => (
                        <TodoItem
                            key={todo.id}
                            eventId={props.eventId}
                            value={todo}
                            complete={complete}
                            remove={remove}
                            editNote={editNote}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );

    const handleAddTodo = () => {
        navigate(`/event/${props.eventId}/map`, { state: { enableTodoMode: true } });
    };

    return (
        <Stack
            direction="column"
            spacing={2}
            sx={{
                width: '100%',
                height: '100%',
                alignItems: 'center',
            }}
        >
            {todoList}

            {!viewOnly && canEditTodos && (
                <Fab
                    color="primary"
                    onClick={handleAddTodo}
                    sx={{
                        height: 56,
                        width: 56,
                        position: 'fixed',
                        bottom: bottomPadding + 16,
                        right: 16,
                        zIndex: 10,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Stack>
    );
}
